import * as React from 'react';
import GeneralTab from './GeneralTab';
import WorkItemTab from './WorkItemTab';
import ValidationTab from './ValidationTab';
import '../AnalyticsView/CreateAnalyticsViewStyle.css';
import {
  FontIcon,
  INavLink,
  INavLinkGroup,
  INavStyles,
  Nav,
  Spinner,
  SpinnerSize,
} from '@fluentui/react';
import {
  WorkItemTypes,
  AreaPathFilter,
  AnalyticsFieldFilter,
  Definition,
  FieldSet,
  GeneralAnalytics,
  ProjectTeamFilter,
  ViewAnalyticsModel,
  HistoryConfiguration,
  FieldFilter,
  AnalyticsField,
  AnalyticsViewAreaPath,
  GeneralTabValidationInfo,
  FieldFilterData,
} from './AnalyticsModel';
import { AnalyticsActions } from './AnalyticsActions';
import FieldSelectionTab from './FieldSelectionTab';
import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { HistoryTab } from './HistoryTab';
import moment from 'moment';
import { v1 as uniqueID } from 'uuid';
import { ServiceFactoryClientExecuteApi } from 'src/Common/Services/ServiceFactoryToExecuteApi';
import { HttpVerbs, UATConstants } from 'src/Common/Models/UATConstants';
import ErrorResponse from 'src/Models/ErrorResponse';
import { ErrorMessage } from 'src/App/Components/Common/ErrorMessage';
import { DisplayColumn } from 'src/Models/DisplayColumns';
import { appInsights } from 'src/Services/Insights';
import { GetUserAppInsightProperties } from 'src/utils/AppInsightProperties';

const navStyles: Partial<INavStyles> = {
  root: {
    width: 178,
    height: 350,
    boxSizing: 'border-box',
    border: '1px solid #eee',
    overflowY: 'auto',
    marginTop: '15px',
  },
  link: {
    whiteSpace: 'normal',
    lineHeight: 'inherit',
  },
};

const navLinkGroups: INavLinkGroup[] = [
  {
    links: [
      {
        name: 'General',
        url: '',
        key: 'Generalkey',
      },
      {
        name: 'Work Items',
        url: '',
        key: 'Workitemkey',
      },
      {
        name: 'Fields',
        url: '',
        key: 'Fieldskey',
      },
      {
        name: 'History',
        url: '',
        key: 'Historykey',
      },
      {
        name: 'Verification',
        url: '',
        key: 'ValidateKey',
      },
    ],
  },
];

interface Props {
  viewId?: string;
  formMode: string;
  closePanel: () => void;
  onShowMessage: (message: string) => void;
  updatePanelState: (activeTab: string, isFormValid: boolean, showSpinner: boolean) => void;
}

export interface CreateAnalyticsViewRef {
  handleContinue: () => void;
  handleSubmit: () => void;
}

const CreateAnalyticsView = forwardRef<CreateAnalyticsViewRef, Props>((props, ref) => {
  //Initialization
  const initialGData: GeneralAnalytics = {
    title: '',
    description: '',
    viewType: 0,
  };

  const initialfieldsData: FieldSet = {
    fieldType: 1,
    fields: getDefaultFields(),
  };
  const initareaPathFilter: AreaPathFilter[] = [];
  const initProjectTeamFilter: ProjectTeamFilter[] = [];
  const initWorkItemTypes: WorkItemTypes = {
    mode: 0,
    operator: 0,
    values: [UATConstants.WorkItemType],
  };
  const initialHistoryConfigurationData: HistoryConfiguration = {
    historyType: 0,
    trendGranularity: { granularityType: null, weeklyEndDay: null },
    rollingDays: null,
    dateRange: {
      startDate: moment(new Date()).subtract(1, 'months').toDate(),
      endDate: null,
    },
    excludeOldCompletedWorkItems: false,
    oldCompletedItemsCutoffDate: null,
  };
  const [showSpinner, setSpinner] = useState<boolean>(false);
  const [isSaveSuccess, setIsSaveSuccess] = useState<boolean>(false);
  const [createdView, setCreatedView] = useState<any>([]);
  const [activeTab, setActiveTab] = useState<string>(props.formMode === 'New' ? 'Generalkey' : '');

  const [generalData, setGeneralData] = useState<GeneralAnalytics>(initialGData);
  const [fieldsData, setFieldsData] = useState<FieldSet>(initialfieldsData);
  const [viewCriteriaData, setViewCriteriaData] = useState<any>();
  const [avFields, setAVFields] = useState<AnalyticsField[]>([]);
  const [assignedTeam, setAssignedTeam] = useState<any[]>([]);
  const [tagOptions, setTagOptions] = useState<any[]>([]);
  const [isTeamFilterBySelectionMode, setIsTeamFilterBySelectionMode] = useState<boolean>(true);
  const [historyConfiguration, setHistoryConfiguration] = useState<HistoryConfiguration>(
    initialHistoryConfigurationData
  );

  const [formMode, setFormMode] = useState<string>(props.formMode || 'New');

  //Validation
  const [isGeneralTabValid, setIsGeneralTabValid] = useState<boolean>(true);
  const [isWorkItemTabValid, setIsWorkItemTabValid] = useState<boolean>(true);
  const firstRender = React.useRef(true);
  //ValidationObjects
  const [generalTabValidationInfo, setGeneralTabValidationInfo] =
    useState<GeneralTabValidationInfo>({
      titleIsValid: true,
      descriptionIsValid: true,
    });
  const [error, setError] = useState<string>('');

  //Functions
  function _onLinkClick(ev?: React.MouseEvent<HTMLElement>, item?: INavLink) {
    if (item && item.key) {
      //validate General Tab
      validateTabs();
      setActiveTab(item.key as string);
    }
  }

  function getDefaultFields() {
    const fields: string[] = [];
    UATConstants.DefaultAnalyticsViewColumnOptions.map((item: DisplayColumn) => {
      fields.push(item.internalName);
    });
    return fields;
  }

  function validateTabs() {
    const g = generalTabValidationInfo;
    g.titleIsValid = generalData.title.trim().length > 0;
    setGeneralTabValidationInfo(g);
    setIsGeneralTabValid(generalData.title.trim().length > 0);
    setIsWorkItemTabValid(validateFieldCriteriaValues());
  }

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    validateTabs();
  }, [generalData]);

  function IsFormValid() {
    return isGeneralTabValid && isWorkItemTabValid;
  }

  const renderLink = (link: INavLink): JSX.Element => {
    const linkKey = link.key as string;

    if (linkKey === 'Generalkey') {
      return (
        <>
          {link.name}
          {!isGeneralTabValid && (
            <FontIcon iconName={UATConstants.PlaceholdersAndTitles.Error} className='errorIcon' />
          )}
        </>
      );
    }
    if (linkKey === 'Workitemkey') {
      return (
        <>
          {link.name}
          {!isWorkItemTabValid && (
            <FontIcon iconName={UATConstants.PlaceholdersAndTitles.Error} className='errorIcon' />
          )}
        </>
      );
    } else {
      return <>{link.name}</>;
    }
  };

  useEffect(() => {
    if (formMode === 'Edit' && props.viewId !== undefined) {
      AnalyticsActions.GetAnalyticsViewById(props.viewId, (viewData: ViewAnalyticsModel) => {
        const gData: GeneralAnalytics = {
          title: viewData.name,
          description: viewData.description,
          viewType: viewData.visibility,
        };

        const fieldsData: FieldSet = {
          fieldType: viewData.definition.fieldSet.fieldType,
          fields: viewData.definition.fieldSet.fields,
        };
        setGeneralData(gData);
        setFieldsData(fieldsData);

        const fieldCriteria: FieldFilterData[] = viewData.definition.fieldFilters;
        const viewCriteria: AnalyticsFieldFilter = new AnalyticsFieldFilter();

        let indx: number = 0;
        if (fieldCriteria !== null) {
          fieldCriteria.forEach((item: FieldFilterData) => {
            const clauseNew: FieldFilter = {
              id: uniqueID(),
              index: indx++,
              fieldName: item.fieldName,
              fieldOperation: item.fieldOperation,
              value: {
                mode: item.value.mode,
                operator: item.value.operator,
                values: item.value.values,
              },
              fieldValue: item.fieldValue,
              fieldValuesOperator: item.fieldValuesOperator,
              fieldDisplayName: item.fieldDisplayName,
              unknownPropertyDisplayName: null,
              fieldErrorMessage: null,
              valueErrorMessage: null,
              unknownValueText: null,
            };

            viewCriteria.fieldFilters.push(clauseNew);
          });
        }
        const historyConfigurationData: HistoryConfiguration =
          viewData.definition.historyConfiguration;
        setHistoryConfiguration(historyConfigurationData);
        setViewCriteriaData(viewCriteria);
        setActiveTab('Generalkey'); //This one is making the data visible for the first time!
      });
    }
  }, []);

  useEffect(() => {
    setSpinner(true);
    getADOTags();
    getAreaPathoptions();
    AnalyticsActions.GetAnalyticsViewFields(onFieldsDataReceived);
  }, []);

  const onFieldsDataReceived = (response: AnalyticsField[]) => {
    setAVFields(response);
    setSpinner(false);
  };

  const getADOTags = () => {
    ServiceFactoryClientExecuteApi.ExecuteAdoRequest<any>(
      UATConstants.ApiPaths.GetAnalyticsViewTags,
      [],
      HttpVerbs.get,
      [],
      (fields: any[]) => {
        setTagOptions(fields);
      },
      (response: ErrorResponse) => {
        console.error(response);
      },
      false
    );
  };

  const getAreaPathoptions = () => {
    if (assignedTeam.length === 0) {
      const options: any[] = [];
      AnalyticsActions.GetAnalyticsViewAreaPath((areaPaths) => {
        areaPaths.forEach((item: AnalyticsViewAreaPath) => {
          options.push({
            id: item.Number,
            areaPathName: item.AreaName,
            areaPath: item.AreaPath,
            areaPathCustom: item.AreaPath.replace(/\\/g, ' ')
              .replace(/@/g, '')
              .replace(UATConstants.Project, ''),
            areaId: item.AreaId,
          });
        });
        setAssignedTeam(options);
      });
    }
  };
  const handleContinue = () => {
    const currentIndx: number = navLinkGroups[0].links.findIndex(
      (x) => (x.key as string) === activeTab
    );
    //Validate Each tab  and set state
    validateTabs();
    setActiveTab(navLinkGroups[0].links[currentIndx + 1].key as string);
  };
  const handleSubmit = () => {
    setSpinner(true);
    const newAnalyticsView: ViewAnalyticsModel = formAnalyticView();

    //API Call based on form type
    setError('');
    if (formMode === 'Edit') {
      const avId: string = props.viewId ? props.viewId : '';
      AnalyticsActions.UpdateAnalyticsView(
        newAnalyticsView,
        avId,
        onRequestSucess,
        onRequestFailure
      );
    }
    if (formMode === 'New') {
      AnalyticsActions.PostAnalyticsView(newAnalyticsView, onRequestSucess, onRequestFailure);
    }
  };

  const formAnalyticView = (): ViewAnalyticsModel => {
    let fieldCriteria: FieldFilterData[] = [];
    if (viewCriteriaData !== undefined) {
      //Remove Id and form the object
      fieldCriteria = viewCriteriaData?.fieldFilters
        ?.filter((x: any) => x.fieldName !== '')
        .map((item: FieldFilter) => ({
          fieldName: item.fieldName,
          fieldOperation: item.fieldOperation,
          value: item.value,
          fieldValue: item.fieldValue,
          fieldValuesOperator: item.fieldValuesOperator,
          fieldDisplayName: item.fieldDisplayName,
          unknownPropertyDisplayName: item.unknownPropertyDisplayName,
          fieldErrorMessage: item.fieldErrorMessage,
          valueErrorMessage: item.valueErrorMessage,
          unknownValueText: item.unknownValueText,
        }));
    }

    const definition: Definition = {
      areaPathFilters: initareaPathFilter, //Default value
      projectTeamFilters: initProjectTeamFilter, //Default value
      fieldFilters: fieldCriteria,
      workItemTypes: initWorkItemTypes,
      backlogs: [],
      isTeamFilterBySelectionMode: isTeamFilterBySelectionMode,
      fieldSet: fieldsData,
      historyConfiguration: historyConfiguration,
    };

    const newAnalyticsView: ViewAnalyticsModel = {
      name: generalData.title,
      description: generalData.description,
      visibility: generalData.viewType,
      viewType: 0,
      definition: definition,
    };

    return newAnalyticsView;
  };

  const validateFieldCriteriaValues = (): boolean => {
    let isValid = true;
    const viewCriteria: AnalyticsFieldFilter = viewCriteriaData;
    if (viewCriteria)
      if (viewCriteria && viewCriteria.fieldFilters && viewCriteria.fieldFilters.length > 0) {
        for (var i = 0; i < viewCriteria.fieldFilters.length; i++) {
          const foundColumn = avFields.find(
            (x) => x.propertyName === viewCriteria.fieldFilters[i].fieldName
          );

          if (foundColumn?.type === 'Edm.Int32') {
            //validate value for digits
            const fldValue = viewCriteria.fieldFilters[i].value.values[0];
            if (parseInt(fldValue).toString() === fldValue) {
              isValid = true;
            } else {
              isValid = false;
            }
          }
          if (foundColumn?.type === 'Edm.Int64') {
            //validate value for digits
            const fldValue = viewCriteria.fieldFilters[i].value.values[0];
            const regex = /^\d+(\.\d{1,2})?$/;
            if (regex.test(fldValue)) {
              isValid = true;
            } else {
              isValid = false;
            }
          }
          if (!isValid) {
            break;
          }
        }
      }
    return isValid;
  };

  const onRequestFailure = (erorr: ErrorResponse) => {
    setError(erorr?.detailedErrorMessages?.['detail']);
    setSpinner(false);
  };
  const onRequestSucess = (result: any) => {
    setSpinner(false);
    if (result !== undefined) {
      setCreatedView(result);
      props.closePanel();
      props.onShowMessage(
        props.formMode === 'New'
          ? UATConstants.AnalyticViewText.addedSuccessMessage
          : UATConstants.AnalyticViewText.updatedSucessMessage
      );
      if (props.formMode === 'New') {
        appInsights.trackEvent(
          {
            name: 'UAT-AnalyticsView-Added',
            properties: {
              viewName: generalData.title,
              viewCreatedDate: new Date().toISOString(),
            },
          },
          GetUserAppInsightProperties()
        );
      } else {
        appInsights.trackEvent(
          {
            name: 'UAT-AnalyticsView-Updated',
            properties: {
              viewName: generalData.title,
              viewUpdatedDate: new Date().toISOString(),
            },
          },
          GetUserAppInsightProperties()
        );
      }
    }
  };

  const updateGeneralDataInParent = (gGata: GeneralAnalytics) => {
    if (!firstRender.current) {
      setGeneralData(gGata);
    }
  };

  const updateFieldsInParent = (fSet: FieldSet) => {
    setFieldsData({ ...fSet });
  };

  const updateCriteriaInParent = (viewCriteria: AnalyticsFieldFilter) => {
    setViewCriteriaData({ ...viewCriteria });
  };

  const updateHistoryInParent = (viewHistConfig: HistoryConfiguration) => {
    setHistoryConfiguration(viewHistConfig);
  };

  // Notify parent component about state changes
  useEffect(() => {
    props.updatePanelState(activeTab, IsFormValid(), showSpinner);
  }, [activeTab, isGeneralTabValid, isWorkItemTabValid, showSpinner]);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    handleContinue,
    handleSubmit,
  }));

  return (
    <div className='createActionContainer'>
      <div>
        {showSpinner && (
          <Spinner
            size={SpinnerSize.large}
            label={UATConstants.PlaceholdersAndTitles.Loading}
            ariaLive='assertive'
            className='loading-panel-full'
          />
        )}
      </div>

      <>
        {!isSaveSuccess && (
          <>
            <div className='flex-container'>
              <div className='flex-child'>
                <Nav
                  onLinkClick={_onLinkClick}
                  selectedKey={activeTab}
                  ariaLabel={UATConstants.PlaceholdersAndTitles.Tabs}
                  styles={navStyles}
                  onRenderLink={renderLink}
                  groups={navLinkGroups}
                />
              </div>
              <div className='flex-child'>
                {error !== '' && (
                  <ErrorMessage
                    containerClassName='av-error-message'
                    onDismiss={() => {
                      setError('');
                    }}>
                    {error}
                  </ErrorMessage>
                )}
                {activeTab === 'Generalkey' && (
                  <GeneralTab
                    generalInput={generalData}
                    sendBackToParent={updateGeneralDataInParent}
                    validationInfo={generalTabValidationInfo}
                  />
                )}
                {activeTab === 'Fieldskey' && (
                  <FieldSelectionTab
                    fieldsInput={fieldsData}
                    updateFieldsInParent={updateFieldsInParent}
                    allAVFields={avFields}
                  />
                )}
                {activeTab === 'Workitemkey' && (
                  <WorkItemTab
                    criteriaInput={viewCriteriaData}
                    updateCriteriaInParent={updateCriteriaInParent}
                    allAVFields={avFields}
                    tagOptions={tagOptions}
                    assignedTeam={assignedTeam}
                  />
                )}
                {activeTab === 'Historykey' && (
                  <HistoryTab
                    sendBackToParent={updateHistoryInParent}
                    initialHistoryModel={historyConfiguration}></HistoryTab>
                )}
                {activeTab === 'ValidateKey' && (
                  <ValidationTab
                    analyticsViewData={formAnalyticView()}
                    readyToValid={IsFormValid()}></ValidationTab>
                )}
              </div>
            </div>
          </>
        )}
        {isSaveSuccess && (
          <>
            <div className='responseSubmitted'>
              <div className='completedDiv'>
                <div className='height100'></div>
                <FontIcon
                  iconName={UATConstants.BulkUploadUserMessages.Completed}
                  className='completedImageColor'
                />
                <span className='thanksStyle'>{UATConstants.PlaceholdersAndTitles.Thanks}</span>
                <br />
                <div className='height20'></div>
                <div className='ligthGrayColor'>
                  {props.formMode === 'New'
                    ? UATConstants.AnalyticViewText.addedSuccessMessage
                    : UATConstants.AnalyticViewText.updatedSucessMessage}
                </div>
                <div className='height20'></div>
              </div>
            </div>
          </>
        )}
      </>
    </div>
  );
});

CreateAnalyticsView.displayName = 'CreateAnalyticsView';

export default CreateAnalyticsView;
